"use client"

import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"

/**
 * CAC = spend ÷ new customers acquired.
 * - Paid CAC: paid ad spend ÷ customers attributed to paid.
 * - Blended CAC: total sales + marketing spend ÷ ALL new customers (incl. organic).
 * Default paid example: $10,000 ÷ 200 = $50 CAC.
 */
export function CacWidget() {
  // Paid mode
  const [paidSpend, setPaidSpend] = useState("10000")
  const [paidCustomers, setPaidCustomers] = useState("200")
  // Blended mode
  const [totalSpend, setTotalSpend] = useState("30000")
  const [totalCustomers, setTotalCustomers] = useState("500")

  const paidCac = (() => {
    const s = parseNum(paidSpend)
    const c = parseNum(paidCustomers)
    return s !== null && c !== null && c > 0 ? s / c : NaN
  })()

  const blendedCac = (() => {
    const s = parseNum(totalSpend)
    const c = parseNum(totalCustomers)
    return s !== null && c !== null && c > 0 ? s / c : NaN
  })()

  return (
    <CalculatorCard title="CAC Calculator">
      <Tabs defaultValue="paid">
        <TabsList className="mb-6">
          <TabsTrigger value="paid">Paid CAC</TabsTrigger>
          <TabsTrigger value="blended">Blended CAC</TabsTrigger>
        </TabsList>

        <TabsContent value="paid">
          <CalculatorGrid>
            <NumberField
              id="cac-paid-spend"
              label="Paid ad spend"
              value={paidSpend}
              onChange={setPaidSpend}
              prefix="$"
            />
            <NumberField
              id="cac-paid-customers"
              label="Customers from paid"
              value={paidCustomers}
              onChange={setPaidCustomers}
              min={0}
              step={1}
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Paid CAC"
              value={formatCurrency(paidCac)}
              caption={
                Number.isFinite(paidCac)
                  ? `It costs ${formatCurrency(paidCac)} to acquire one customer through paid channels.`
                  : "Enter spend and a non-zero customer count."
              }
            />
          </div>
          <FormulaNote>Paid CAC = Paid ad spend ÷ Customers attributed to paid.</FormulaNote>
        </TabsContent>

        <TabsContent value="blended">
          <CalculatorGrid>
            <NumberField
              id="cac-total-spend"
              label="Total sales + marketing spend"
              value={totalSpend}
              onChange={setTotalSpend}
              prefix="$"
            />
            <NumberField
              id="cac-total-customers"
              label="All new customers (incl. organic)"
              value={totalCustomers}
              onChange={setTotalCustomers}
              min={0}
              step={1}
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Blended CAC"
              value={formatCurrency(blendedCac)}
              caption={
                Number.isFinite(blendedCac)
                  ? `Across every channel, you pay ${formatCurrency(blendedCac)} per new customer on average.`
                  : "Enter spend and a non-zero customer count."
              }
            />
          </div>
          <FormulaNote>Blended CAC = Total sales + marketing spend ÷ All new customers (paid + organic).</FormulaNote>
        </TabsContent>
      </Tabs>
    </CalculatorCard>
  )
}
